// custom-multifile-input.js
import React from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { Box, Typography, Grid, Paper, IconButton, Tooltip, Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: "#fafbfc",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  headerIcon: {
    color: "#0E2E67",
    marginTop: 2,
  },
  headerText: {
    flex: 1,
  },
  label: {
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  description: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
  },
  metaChip: {
    height: 22,
    fontSize: "0.72rem",
    backgroundColor: "#eef2f8",
    color: "#0E2E67",
    fontWeight: 500,
  },
  dropzone: {
    border: "2px dashed #0E2E67",
    borderRadius: "10px",
    padding: theme.spacing(1),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f9f9f9",
    "&:hover": {
      backgroundColor: "#f1f1f1",
    },
    width: 100,
    height: 100,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  dropzoneIcon: {
    color: "#0E2E67",
    fontSize: 22,
  },
  dropzoneText: {
    fontSize: "0.72rem",
    lineHeight: 1.2,
  },
  fileBox: {
    border: "1px solid #ccc",
    borderRadius: "10px",
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    textAlign: "center",
    position: "relative",
    backgroundColor: "#fff",
    width: 100,
    height: 100,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  fileIcon: {
    color: "#0E2E67",
    fontSize: 22,
    marginBottom: 2,
  },
  fileName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    fontSize: "0.78rem",
  },
  fileSize: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  removeButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: 2,
  },
}));

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const CustomMultiFileInput = ({
  id,
  label,
  description,
  acceptedFileTypes,
  files,
  onFilesChange,
  maxFileSizeMB,
  maxTotalSizeMB,
  maxFiles,
  onValidationError,
}) => {
  const classes = useStyles();

  const reportError = (message) => {
    if (typeof onValidationError === "function") {
      onValidationError(message);
    } else {
      // eslint-disable-next-line no-alert
      window.alert(message);
    }
  };

  const onDrop = (acceptedFiles, fileRejections) => {
    // dropzone-level rejections (wrong type, etc.)
    if (fileRejections && fileRejections.length > 0) {
      const names = fileRejections.map((r) => r.file?.name || "file").join(", ");
      reportError(`Rejected: ${names}. Only ${acceptedFileTypes.join(", ")} files are allowed.`);
    }

    const perFileLimit = maxFileSizeMB * 1024 * 1024;
    const totalLimit = maxTotalSizeMB * 1024 * 1024;

    const valid = [];
    const tooLarge = [];

    for (const f of acceptedFiles) {
      if (f.size > perFileLimit) {
        tooLarge.push(`${f.name} (${formatBytes(f.size)})`);
      } else {
        valid.push(f);
      }
    }

    if (tooLarge.length > 0) {
      reportError(
        `The following file(s) exceed the ${maxFileSizeMB} MB per-file limit and were not added: ${tooLarge.join(", ")}`
      );
    }

    if (valid.length === 0) return;

    // Enforce max file count
    const remainingSlots = Math.max(0, maxFiles - files.length);
    const accepted = valid.slice(0, remainingSlots);
    if (valid.length > remainingSlots) {
      reportError(`Only up to ${maxFiles} file(s) can be uploaded for "${label}". Extra files were ignored.`);
      if (accepted.length === 0) return;
    }

    // Enforce total size budget
    const currentTotal = files.reduce((sum, f) => sum + (f.size || 0), 0);
    let runningTotal = currentTotal;
    const finalAccepted = [];
    const overflow = [];
    for (const f of accepted) {
      if (runningTotal + (f.size || 0) > totalLimit) {
        overflow.push(`${f.name} (${formatBytes(f.size)})`);
        continue;
      }
      runningTotal += f.size || 0;
      finalAccepted.push(f);
    }

    if (overflow.length > 0) {
      reportError(
        `Total upload size for "${label}" cannot exceed ${maxTotalSizeMB} MB. Skipped: ${overflow.join(", ")}`
      );
    }

    if (finalAccepted.length > 0) {
      onFilesChange([...files, ...finalAccepted]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes.join(","),
    maxSize: maxFileSizeMB * 1024 * 1024,
  });

  const handleRemoveFile = (fileName) => {
    onFilesChange(files.filter((file) => file.name !== fileName));
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <CloudUploadOutlinedIcon className={classes.headerIcon} />
        <Box className={classes.headerText}>
          <Typography className={classes.label}>{label}</Typography>
          {description && (
            <Typography className={classes.description}>{description}</Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.metaRow}>
        <Chip className={classes.metaChip} label={`Accepted: ${acceptedFileTypes.join(", ")}`} />
        <Chip className={classes.metaChip} label={`Max ${maxFileSizeMB} MB / file`} />
        <Chip className={classes.metaChip} label={`Up to ${maxTotalSizeMB} MB total`} />
        <Chip className={classes.metaChip} label={`Max ${maxFiles} files`} />
        <Chip
          className={classes.metaChip}
          label={`${files.length} / ${maxFiles} uploaded`}
        />
      </Box>

      <Grid container spacing={1} alignItems="center" justify="flex-start">
        {files.map((file) => (
          <Grid item xs={6} sm={3} md={2} key={file.name}>
            <Paper className={classes.fileBox}>
              <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
              <Tooltip title={`${file.name} (${formatBytes(file.size)})`} arrow>
                <Typography variant="body2" className={classes.fileName}>
                  {file.name}
                </Typography>
              </Tooltip>
              <Typography variant="caption" className={classes.fileSize}>
                {formatBytes(file.size)}
              </Typography>
              <IconButton
                size="small"
                className={classes.removeButton}
                onClick={() => handleRemoveFile(file.name)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        ))}
        {files.length < maxFiles && (
          <Grid item xs={6} sm={3} md={2}>
            <Box {...getRootProps({ className: classes.dropzone })}>
              <input {...getInputProps()} id={id} />
              <CloudUploadOutlinedIcon className={classes.dropzoneIcon} />
              <Typography className={classes.dropzoneText}>
                Drag & drop or click
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

CustomMultiFileInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  files: PropTypes.arrayOf(PropTypes.object).isRequired,
  onFilesChange: PropTypes.func.isRequired,
  maxFileSizeMB: PropTypes.number,
  maxTotalSizeMB: PropTypes.number,
  maxFiles: PropTypes.number,
  onValidationError: PropTypes.func,
};

CustomMultiFileInput.defaultProps = {
  description: "",
  maxFileSizeMB: 10,
  maxTotalSizeMB: 30,
  maxFiles: 5,
  onValidationError: null,
};

export default CustomMultiFileInput;
